import * as React from 'react';
import { Box, Skeleton, Alert, Typography, Button, CircularProgress, IconButton, Popover, useMediaQuery } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import axios from 'axios';
import { withTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import useCalendarState from './hooks/useCalendarState';
import CalendarHeader from './CalendarHeader';
import CalendarSidebar from './CalendarSidebar';
import CalendarGrid from './CalendarGrid';
import CalendarDayGrid from './CalendarDayGrid';
import CalendarMonthGrid from './CalendarMonthGrid';
import CalendarLegend, { CalendarLegendContent } from './CalendarLegend';
import DragContextMenu from './DragContextMenu';
import EventContextMenu from './EventContextMenu';
import ScheduleDialog from './ScheduleDialog';
import UnavailabilityDialog from './UnavailabilityDialog';
import CalendarNoteDialog from './CalendarNoteDialog';
import GoogleCalendarAuthBanner from './GoogleCalendarAuthBanner';
import { useUserStore } from 'store';

import { can, isSimplifiedUi, ownCalendarOnly } from '../../utils/access';
import { FILTER_NONE } from './calendarConstants';
import { parseDateKey } from './hooks/dateUtils';
import { customerLabel } from '../../utils/customer';
function MainCalendarView({ t }) {
  const {
    loading,
    error,
    currentView,
    setCurrentView,
    calendarMode,
    setCalendarMode,
    selectedDay,
    switchToDay,
    setLoading,
    setError,
    setCalendarData,
    setUnscheduledData,
    getWeekRange,
    getMonthKey,
    setMonthData,
    setSelectedMonthDay,
    monthVersion,
    monthOpenHours,
    filters,
    currentDate,
    employees,
    workSchedule,
    setCurrentDateFromString,
    setHighlightedMaintenance,
    clearHighlightedMaintenance,
    setHighlightedUnscheduled,
    clearHighlightedUnscheduled,
    highlightedUnscheduledId,
    sidebarCollapsed,
    toggleSidebar,
    initializePreferences,
    legendHidden: legendHiddenPref,
    setLegendHidden,
    schedulingModeTarget,
    enterSchedulingMode,
    exitSchedulingMode,
  } = useCalendarState();

  const { currentOrganization } = useUserStore();
  const [searchParams, setSearchParams] = useSearchParams();

  const [refreshKey, setRefreshKey] = React.useState(0);
  const [preferencesLoaded, setPreferencesLoaded] = React.useState(false);
  // A failed month fetch is shown in place of the month grid, not as the full-page error:
  // the mode is remembered, so the header with the mode switch has to stay reachable.
  const [monthError, setMonthError] = React.useState(false);

  // Detect short viewport for compact mode (14" laptops with scaling)
  const isShortScreen = useMediaQuery('(max-height: 900px)');
  const [legendAnchorEl, setLegendAnchorEl] = React.useState(null);
  // null = follow screen size default, true/false = user explicitly toggled
  const legendHidden = legendHiddenPref !== null ? legendHiddenPref : isShortScreen;

  // Load preferences from localStorage on mount (org-specific)
  React.useEffect(() => {
    if (currentOrganization && !preferencesLoaded) {
      // Initialize preferences from localStorage
      initializePreferences();

      // Check if we have saved preferences by looking at the current state
      // If currentView is still the default 'employee' after initialization,
      // and no localStorage entry exists, fall back to role-based default
      const orgId = currentOrganization.organization_id;
      const storageKey = `calendar_preferences_${orgId}`;
      const hasSavedPrefs = localStorage.getItem(storageKey);

      if (!hasSavedPrefs) {
        // No saved preferences: everyone's rows, or the own row without calendar.view_all
        setCurrentView(ownCalendarOnly(currentOrganization) ? 'employee' : 'all');
      }

      setPreferencesLoaded(true);
    }
  }, [currentOrganization, preferencesLoaded, initializePreferences, setCurrentView]);

  // Header controls: the resource switcher follows calendar.view_all, the create button the UI mode
  const hideResourceSwitcher = ownCalendarOnly(currentOrganization);
  const hideCreateButton = isSimplifiedUi(currentOrganization) || !can(currentOrganization, 'work_orders.create');

  // A ?highlight= / ?highlightUnscheduled= link marks the work order for 5 seconds counted
  // from when the calendar has loaded, not from the click: a slow fetch used to use up the time
  // behind the skeleton or the loading overlay, and the card turned up unmarked. Every load
  // restarts the count. Leaving the calendar clears what is still marked.
  const urlHighlightRef = React.useRef({ maintenanceId: null, unscheduledId: null });
  // StrictMode runs the mount effect twice, and the second run would find the params
  // already consumed. Once per mount is what the effect means.
  const urlParamsHandledRef = React.useRef(false);

  // Handle URL params on mount (for navigation from maintenance detail)
  React.useEffect(() => {
    if (urlParamsHandledRef.current) {
      // StrictMode's simulated unmount cleared the link's highlight (the effect below): put it back
      const { maintenanceId, unscheduledId } = urlHighlightRef.current;
      if (maintenanceId) setHighlightedMaintenance(maintenanceId);
      if (unscheduledId) setHighlightedUnscheduled(unscheduledId);
      return undefined;
    }
    urlParamsHandledRef.current = true;

    const dateParam = searchParams.get('date');
    const highlightParam = searchParams.get('highlight');
    const highlightUnscheduledParam = searchParams.get('highlightUnscheduled');
    const scheduleMaintenanceIdParam = searchParams.get('scheduleMaintenanceId');
    const viewParam = searchParams.get('view');
    const modeParam = searchParams.get('mode');

    // Handle mode param (day/week/month)
    if (modeParam === 'day') {
      if (dateParam) {
        switchToDay(new Date(dateParam));
        searchParams.delete('date');
      } else {
        setCalendarMode('day');
      }
      searchParams.delete('mode');
    } else if (modeParam === 'week' || modeParam === 'month') {
      setCalendarMode(modeParam);
      searchParams.delete('mode');
    }

    if (dateParam && modeParam !== 'day') {
      setCurrentDateFromString(dateParam);
      searchParams.delete('date');
    }

    // Set view mode if specified in URL (e.g., 'all', 'employee', 'service_bay')
    if (viewParam && ['all', 'employee', 'service_bay'].includes(viewParam)) {
      setCurrentView(viewParam);
      searchParams.delete('view');
    }

    if (highlightParam) {
      const maintenanceId = parseInt(highlightParam, 10);
      setHighlightedMaintenance(maintenanceId);
      urlHighlightRef.current.maintenanceId = maintenanceId;
      // The month grid draws no cards to highlight: open the linked day's peek, whose
      // list marks the work order. The mode is read from the store because the
      // preferences effect above has just restored it.
      if (dateParam && parseDateKey(dateParam) && useCalendarState.getState().calendarMode === 'month') {
        setSelectedMonthDay(dateParam);
      }
      searchParams.delete('highlight');
      // Update URL without the params
      setSearchParams(searchParams, { replace: true });
      return;
    }

    if (highlightUnscheduledParam) {
      // Expand sidebar if collapsed so user can see the highlighted item
      if (sidebarCollapsed) {
        toggleSidebar();
      }
      const unscheduledId = parseInt(highlightUnscheduledParam, 10);
      setHighlightedUnscheduled(unscheduledId);
      urlHighlightRef.current.unscheduledId = unscheduledId;
      searchParams.delete('highlightUnscheduled');
      // Update URL without the params
      setSearchParams(searchParams, { replace: true });
      return;
    }

    if (scheduleMaintenanceIdParam) {
      const maintenanceId = parseInt(scheduleMaintenanceIdParam, 10);
      searchParams.delete('scheduleMaintenanceId');
      setSearchParams(searchParams, { replace: true });
      // Fetch maintenance data and enter scheduling mode
      axios.get(`/api/maintenances/${maintenanceId}/`).then((response) => {
        const m = response.data;
        enterSchedulingMode({
          id: m.id,
          work_number: m.work_number,
          vehicle_registration: m.vehicle?.registration_number || '',
          customer_name: customerLabel(m.customer),
          description: m.description || '',
          employee_ids: (m.employees || []).map(e => e.id),
        });
      }).catch(() => {
        setError(t('Failed to load work order for scheduling'));
      });
      return;
    }

    if (dateParam || highlightParam || highlightUnscheduledParam || viewParam) {
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only while the store still shows the link's own highlight: a calendar search run in the
  // meantime has taken it over (it narrows to one slot, so highlightedScheduleId is set).
  const clearUrlHighlight = React.useCallback(() => {
    const { maintenanceId, unscheduledId } = urlHighlightRef.current;
    const state = useCalendarState.getState();
    if (maintenanceId && state.highlightedMaintenanceId === maintenanceId && state.highlightedScheduleId == null) {
      clearHighlightedMaintenance();
    }
    if (unscheduledId && state.highlightedUnscheduledId === unscheduledId) {
      clearHighlightedUnscheduled();
    }
  }, [clearHighlightedMaintenance, clearHighlightedUnscheduled]);

  React.useEffect(() => {
    const { maintenanceId, unscheduledId } = urlHighlightRef.current;
    if (loading || (!maintenanceId && !unscheduledId)) return undefined;
    const timer = setTimeout(() => {
      clearUrlHighlight();
      urlHighlightRef.current = { maintenanceId: null, unscheduledId: null };
    }, 5000);
    return () => clearTimeout(timer);
  }, [loading, clearUrlHighlight]);

  // Not left behind for the mobile calendar (a tablet turned upright) or the next visit
  React.useEffect(() => clearUrlHighlight, [clearUrlHighlight]);

  // Month mode sends the employee filter to the server (the aggregates cannot be filtered
  // in the browser); the other filters only touch the day peek. Refetch when it changes.
  const monthEmployeeFilter = calendarMode === 'month'
    ? filters.employees.filter((id) => id !== FILTER_NONE).join(',')
    : '';
  // Local event / absence / note changes make the month aggregates stale.
  const monthRefreshVersion = calendarMode === 'month' ? monthVersion : 0;
  // The "does an N h job fit" threshold decides is_open, open_days and next_free_day.
  const monthOpenHoursParam = calendarMode === 'month' ? monthOpenHours : null;
  const openWeekdays = workSchedule.weekdays.join(',');

  // Fetch calendar data when date, view, or organization changes
  React.useEffect(() => {
    // Don't fetch if organization isn't set yet
    if (!currentOrganization) {
      return;
    }

    let cancelled = false;

    const fetchCalendarData = async () => {
      setLoading(true);
      setError(null);
      setMonthError(false);

      try {
        if (calendarMode === 'month') {
          const params = { month: getMonthKey() };
          if (monthEmployeeFilter) {
            params.employee_ids = monthEmployeeFilter;
          }
          if (monthOpenHoursParam) {
            params.open_hours = monthOpenHoursParam;
          }
          // The month payload carries no service bays or colour tags, and the filter
          // panel, the legend and the day peek need them: fetch today's calendar once
          // when the store has not been seeded by the week or day view yet.
          const needsResources = useCalendarState.getState().employees.length === 0;
          const todayKey = getWeekRange().start;
          const [monthResponse, calendarResponse] = await Promise.all([
            axios.get('/api/scheduling/calendar/month/', { params }),
            needsResources
              ? axios.get('/api/scheduling/calendar/', { params: { start: todayKey, end: todayKey, view: 'employee' } })
              : Promise.resolve(null),
          ]);
          if (cancelled) return;
          if (calendarResponse) {
            setCalendarData(calendarResponse.data);
          }
          setMonthData(monthResponse.data);
        } else {
          const { start, end } = getWeekRange();

          const calendarResponse = await axios.get('/api/scheduling/calendar/', {
            params: { start, end, view: currentView },
          });

          if (cancelled) return;
          setCalendarData(calendarResponse.data);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching calendar data:', err);
        if (calendarMode === 'month') {
          setMonthError(true);
        } else {
          setError(err.response?.data?.detail || err.message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCalendarData();
    return () => {
      cancelled = true;
    };
    // openWeekdays: the first fetch runs on the default Mon-Fri week; a workshop open on Saturday
    // fetches again once its own weekdays are known, or the Saturday column would stay empty.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, currentView, calendarMode, selectedDay, refreshKey, currentOrganization, monthEmployeeFilter, monthRefreshVersion, monthOpenHoursParam, openWeekdays]);

  const fetchUnscheduled = React.useCallback(async () => {
    try {
      const unscheduledParams = { limit: 50 };
      const { highlightedUnscheduledId: ensureId } = useCalendarState.getState();
      if (ensureId) {
        unscheduledParams.ensure_id = ensureId;
      }

      const unscheduledResponse = await axios.get('/api/scheduling/calendar/unscheduled/', {
        params: unscheduledParams,
      });

      const unscheduledData = unscheduledResponse.data;
      setUnscheduledData(unscheduledData.results, unscheduledData.total_count, unscheduledData.has_more);
    } catch (err) {
      console.error('Error fetching unscheduled maintenances:', err);
    }
  }, [setUnscheduledData]);

  // Fetch the unscheduled list separately. It does not depend on the visible
  // date range, so it should only refetch on org change or an explicit refresh
  // (e.g. after scheduling/drag actions bump refreshKey) — not on day navigation.
  React.useEffect(() => {
    if (!currentOrganization) {
      return;
    }

    fetchUnscheduled();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey, currentOrganization]);

  // A calendar search can land on a work order that is not in the first page of the
  // unscheduled list, so refetch with ensure_id when one gets highlighted.
  React.useEffect(() => {
    if (!currentOrganization || !highlightedUnscheduledId) {
      return;
    }
    fetchUnscheduled();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightedUnscheduledId, currentOrganization]);

  const handleRetry = () => {
    setRefreshKey((k) => k + 1);
  };

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 64px)',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
        }}
      >
        <Alert severity="error" sx={{ mb: 2, maxWidth: 500 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {t('Error')}
          </Typography>
          <Typography variant="body2">{error}</Typography>
        </Alert>
        <Button variant="contained" onClick={handleRetry}>
          {t('Retry')}
        </Button>
      </Box>
    );
  }

  // Show skeleton on initial load (no data yet) or when waiting for organization
  const isInitialLoad = !currentOrganization || (loading && employees.length === 0 && calendarMode !== 'month');

  if (isInitialLoad) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 1 }} />
        </Box>
        <Box sx={{ flex: 1, display: 'flex' }}>
          <Box sx={{ width: 280, p: 2, borderRight: 1, borderColor: 'divider' }}>
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
          </Box>
          <Box sx={{ flex: 1, p: 2 }}>
            <Skeleton variant="rectangular" height="100%" sx={{ borderRadius: 1 }} />
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      data-testid="calendar-page"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: { xs: 'calc(100vh - 16px)', md: 'calc(100vh - 32px)' },
        overflow: 'hidden',
        position: 'relative',
        mb: { xs: -2, md: -4 },
      }}
    >
      {/* Loading overlay for refreshes */}
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(255, 255, 255, 0.6)',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={40} />
        </Box>
      )}

      {/* Header */}
      <CalendarHeader hideResourceSwitcher={hideResourceSwitcher} hideCreateButton={hideCreateButton} isShortScreen={isShortScreen} />

      {/* Google Calendar authorisation expired: one-click reconnect for admins */}
      <GoogleCalendarAuthBanner />

      {/* Scheduling mode banner */}
      {schedulingModeTarget && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.25,
            mx: 2,
            mb: 0.5,
            bgcolor: 'secondary.main',
            color: 'white',
            borderRadius: '10px',
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '8px',
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              📅
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '14px', lineHeight: 1.3 }}>
                {t('Scheduling mode')}
              </Typography>
              <Typography sx={{ fontSize: '12px', opacity: 0.9, lineHeight: 1.3 }} noWrap>
                {calendarMode === 'month' ? t('calendar.month.clickDayToSchedule') : t('Click and drag on the calendar to schedule')} <strong>#{schedulingModeTarget.work_number}</strong> {schedulingModeTarget.vehicle_registration}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={exitSchedulingMode}
            sx={{
              color: 'white',
              borderColor: 'rgba(255,255,255,0.5)',
              textTransform: 'none',
              fontWeight: 500,
              flexShrink: 0,
              ml: 2,
              '&:hover': {
                borderColor: 'white',
                bgcolor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            {t('Cancel')}
          </Button>
        </Box>
      )}

      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Sidebar: the same unscheduled panel in every mode, collapsible the same way */}
        <CalendarSidebar />

        {/* Calendar Grid - day, week or month mode */}
        {calendarMode === 'month'
          ? monthError
            ? (
              <Box
                data-testid="calendar-month-error"
                sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4 }}
              >
                <Alert severity="error" sx={{ mb: 2, maxWidth: 500 }}>
                  {t('Failed to load data. Please try again.')}
                </Alert>
                <Button variant="contained" onClick={handleRetry}>
                  {t('Retry')}
                </Button>
              </Box>
            )
            : <CalendarMonthGrid isShortScreen={isShortScreen} />
          : calendarMode === 'day'
            ? <CalendarDayGrid isShortScreen={isShortScreen} />
            : <CalendarGrid isShortScreen={isShortScreen} />
        }
      </Box>

      {/* Legend: inline with hide button, or floating popover button when hidden */}
      {legendHidden ? (
        <>
          <IconButton
            data-testid="calendar-legend"
            onClick={(e) => setLegendAnchorEl(e.currentTarget)}
            size="small"
            sx={{
              position: 'fixed',
              bottom: 30,
              right: 90,
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              boxShadow: 2,
              zIndex: 1000,
              width: 36,
              height: 36,
              '&:hover': { bgcolor: 'grey.100' },
            }}
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
          <Popover
            open={Boolean(legendAnchorEl)}
            anchorEl={legendAnchorEl}
            onClose={() => setLegendAnchorEl(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Box data-testid="calendar-legend-popover">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  px: 3,
                  py: 1.5,
                  flexWrap: 'wrap',
                  maxWidth: 600,
                }}
              >
                <CalendarLegendContent t={t} />
              </Box>
              <Box sx={{ px: 3, pb: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  onClick={() => {
                    setLegendHidden(false);
                    setLegendAnchorEl(null);
                  }}
                  sx={{
                    textTransform: 'none',
                    fontSize: '12px',
                    color: 'text.secondary',
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                  }}
                >
                  {t('Show legend')}
                </Button>
              </Box>
            </Box>
          </Popover>
        </>
      ) : (
        <CalendarLegend onHide={() => setLegendHidden(true)} />
      )}

      {/* Dialogs and Overlays */}
      <DragContextMenu />
      <EventContextMenu />
      <ScheduleDialog />
      <UnavailabilityDialog />
      <CalendarNoteDialog />
    </Box>
  );
}

export default withTranslation()(MainCalendarView);
